import React, { useState } from 'react';
import { RotateCcw, ChevronLeft, ChevronRight, CheckCircle2, Sparkles, Brain, BookOpen, Lightbulb, Star, Zap, Award } from 'lucide-react';

// Paletas de degradado por categoría (se ciclan según índice de tarjeta)
const GRADIENTS = [
  { name: 'indigo', from: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 60%, #a855f7 100%)', accent: '#c4b5fd', icon: Sparkles },
  { name: 'esmeralda', from: 'linear-gradient(135deg, #10b981 0%, #059669 55%, #0d9488 100%)', accent: '#a7f3d0', icon: Brain },
  { name: 'ámbar', from: 'linear-gradient(135deg, #f59e0b 0%, #d97706 60%, #f43f5e 100%)', accent: '#fde68a', icon: Lightbulb },
  { name: 'rosa', from: 'linear-gradient(135deg, #ec4899 0%, #db2777 60%, #c026d3 100%)', accent: '#fbcfe8', icon: Star },
  { name: 'cielo', from: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 60%, #6366f1 100%)', accent: '#bae6fd', icon: Zap },
  { name: 'violeta', from: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 60%, #4f46e5 100%)', accent: '#ddd6fe', icon: Award },
];

const pickGradient = (id, index) => {
  const source = typeof id === 'string' && id.startsWith('kp_') ? 3 : 0;
  return GRADIENTS[(source + index) % GRADIENTS.length];
};

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.1rem',
};

const headerRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  flexWrap: 'wrap',
};

const progressTrackStyle = {
  width: '100%',
  height: '8px',
  borderRadius: '999px',
  background: 'rgba(255,255,255,0.08)',
  overflow: 'hidden',
  border: '1px solid rgba(255,255,255,0.06)',
};

const navBtnStyle = (disabled) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(10,26,42,0.7)',
  border: '1px solid rgba(197,160,89,0.25)',
  color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
  borderRadius: '10px',
  padding: '9px 16px',
  fontSize: '0.78rem',
  fontWeight: '600',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease',
});

export const FlashcardsDeck = ({ flashcards = [] }) => {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState({});

  const current = flashcards[index];
  const knownCount = Object.values(known).filter(Boolean).length;
  const allKnown = flashcards.length > 0 && knownCount === flashcards.length;
  const palette = current ? pickGradient(current.id, index) : GRADIENTS[0];
  const Icon = palette.icon;
  const progress = flashcards.length ? ((index + (flipped ? 1 : 0)) / flashcards.length) * 100 : 0;

  const reset = () => {
    setIndex(0);
    setFlipped(false);
    setKnown({});
  };

  const markKnown = () => {
    if (!current) return;
    setKnown((prev) => ({ ...prev, [current.id]: true }));
    setFlipped(false);
    if (index < flashcards.length - 1) setIndex((i) => i + 1);
  };

  if (!current) {
    return (
      <div style={{ ...containerStyle, alignItems: 'center', padding: '2rem 1rem' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '16px',
          background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <BookOpen size={26} style={{ color: 'var(--gold-matte)' }} />
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
          No hay flashcards para esta lección.
        </p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Barra de progreso */}
      <div style={headerRowStyle}>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
          Tarjeta <strong style={{ color: palette.accent }}>{index + 1}</strong> de {flashcards.length} ·{' '}
          <strong style={{ color: '#34d399' }}>{knownCount}</strong> dominadas
        </span>
        <button
          onClick={reset}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent',
            border: '1px solid rgba(197,160,89,0.3)', color: 'var(--text-secondary)',
            borderRadius: '8px', padding: '6px 12px', fontSize: '0.72rem', cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
          }}
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>Reiniciar</span>
        </button>
      </div>

      <div style={{ ...progressTrackStyle, background: 'rgba(255,255,255,0.06)' }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          borderRadius: '999px',
          background: palette.from,
          transition: 'width 0.4s ease',
        }} />
      </div>

      {/* Tarjeta con volteo 3D */}
      <div
        onClick={() => setFlipped((f) => !f)}
        style={{
          perspective: '1400px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{
          position: 'relative',
          width: '100%',
          minHeight: '230px',
          transition: 'transform 0.6s cubic-bezier(0.4, 0.2, 0.2, 1)',
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}>
          {/* FRENTE: PREGUNTA */}
          <div style={{
            position: 'absolute', inset: 0,
            backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
            borderRadius: '20px',
            background: palette.from,
            padding: '2.2rem',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', gap: '14px',
            boxShadow: '0 18px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.25)',
            border: '1px solid rgba(255,255,255,0.2)',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: '-40px', right: '-40px',
              width: '160px', height: '160px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.12)',
            }} />
            <div style={{
              position: 'absolute', bottom: '-60px', left: '-30px',
              width: '140px', height: '140px', borderRadius: '50%',
              background: 'rgba(0,0,0,0.12)',
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                padding: '6px 14px', borderRadius: '999px',
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.35)',
                color: '#fff', fontSize: '0.72rem', fontWeight: '700',
                letterSpacing: '1px', textTransform: 'uppercase',
              }}>
                <Icon size={13} />
                <span>Pregunta</span>
              </div>
              <p style={{
                fontSize: '1.05rem', fontWeight: '700', color: '#fff', margin: '14px 0 0',
                lineHeight: 1.6, textShadow: '0 1px 3px rgba(0,0,0,0.25)',
              }}>
                {current.front}
              </p>
              <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)', margin: '10px 0 0', fontStyle: 'italic' }}>
                Toca para voltear 👆
              </p>
            </div>
          </div>

          {/* REVERSO: RESPUESTA */}
          <div style={{
            position: 'absolute', inset: 0,
            backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            borderRadius: '20px',
            background: 'linear-gradient(160deg, rgba(15, 23, 42, 0.98) 0%, rgba(10, 26, 42, 0.98) 100%)',
            border: `1px solid ${palette.accent}55`,
            padding: '2.2rem',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', gap: '12px',
            boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
            overflowY: 'auto',
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '7px',
              padding: '6px 14px', borderRadius: '999px',
              background: `${palette.accent}1a`,
              border: `1px solid ${palette.accent}55`,
              color: palette.accent, fontSize: '0.72rem', fontWeight: '700',
              letterSpacing: '1px', textTransform: 'uppercase',
            }}>
              <CheckCircle2 size={13} />
              <span>Respuesta</span>
            </div>
            <p style={{
              fontSize: '0.95rem', color: '#e2e8f0', margin: 0,
              lineHeight: 1.7, whiteSpace: 'pre-wrap',
            }}>
              {current.back}
            </p>
          </div>
        </div>
      </div>

      {/* Controles */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button
          disabled={index === 0}
          onClick={() => { setIndex((i) => i - 1); setFlipped(false); }}
          style={navBtnStyle(index === 0)}
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Anterior</span>
        </button>

        <button
          onClick={markKnown}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
            color: '#052e16', border: 'none', borderRadius: '12px',
            padding: '10px 22px', fontSize: '0.8rem', fontWeight: '800', cursor: 'pointer',
            boxShadow: '0 8px 18px rgba(16, 185, 129, 0.35)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
          }}
        >
          <CheckCircle2 className="w-4 h-4" />
          <span>Dominada</span>
        </button>

        <button
          disabled={index >= flashcards.length - 1}
          onClick={() => { setIndex((i) => i + 1); setFlipped(false); }}
          style={navBtnStyle(index >= flashcards.length - 1)}
        >
          <span>Siguiente</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {allKnown && (
        <div style={{
          padding: '14px', borderRadius: '14px', textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.08) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.35)',
          fontSize: '0.82rem', fontWeight: '600', color: '#a7f3d0',
        }}>
          🎉 ¡Dominaste todas las tarjetas de esta lección! 🏆
        </div>
      )}
    </div>
  );
};
